import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AIBusinessChat } from './AIBusinessChat';
import { 
  Sparkles, Wand2, Calculator, MessageCircle, FileText,
  Loader2, Copy, Target, Mail, AlertCircle, Hammer,
  BarChart3, Clock, Eye, Star, Crown, TrendingUp,
  Calendar as CalendarIcon
} from 'lucide-react';

const API = window.location.origin;

export const AIAssistantTab = ({ 
  token,
  customers = [],
  onCreateQuote
}) => {
  // AI Quote Estimation
  const [aiQuoteInput, setAiQuoteInput] = useState({ job_type: 'carpentry', description: '', include_materials: true });
  const [aiQuoteResult, setAiQuoteResult] = useState(null);
  const [aiQuoteLoading, setAiQuoteLoading] = useState(false);
  
  // AI Job Description
  const [aiDescInput, setAiDescInput] = useState({ job_type: 'carpentry', brief_notes: '' });
  const [aiDescResult, setAiDescResult] = useState('');
  const [aiDescLoading, setAiDescLoading] = useState(false);
  
  // AI Auto Reply
  const [aiReplyInput, setAiReplyInput] = useState({ customer_name: '', service_type: 'general-repairs', enquiry_text: '' });
  const [aiReplyResult, setAiReplyResult] = useState('');
  const [aiReplyLoading, setAiReplyLoading] = useState(false);
  
  // AI Business Insights
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  
  // AI Follow-ups
  const [aiFollowUps, setAiFollowUps] = useState([]);
  const [aiFollowUpsLoading, setAiFollowUpsLoading] = useState(false);
  
  // AI Strategic
  const [aiStrategicResult, setAiStrategicResult] = useState(null);
  const [aiStrategicLoading, setAiStrategicLoading] = useState(false);
  
  // AI Outreach
  const [aiOutreachCustomer, setAiOutreachCustomer] = useState('');
  const [aiOutreachType, setAiOutreachType] = useState('re-engagement');
  const [aiOutreachResult, setAiOutreachResult] = useState(null);
  const [aiOutreachLoading, setAiOutreachLoading] = useState(false);
  
  // AI Service Recommendations
  const [aiServiceRecs, setAiServiceRecs] = useState(null);
  const [aiServiceRecsLoading, setAiServiceRecsLoading] = useState(false);
  
  // Weekly Reports
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  const [sendingWeeklyEmail, setSendingWeeklyEmail] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // AI Quote Estimation
  const handleAiQuoteEstimate = async () => {
    if (!aiQuoteInput.description.trim()) {
      toast.error("Please describe the job");
      return;
    }
    setAiQuoteLoading(true);
    setAiQuoteResult(null);
    try {
      const response = await axios.post(`${API}/api/admin/ai/quote-estimation`, aiQuoteInput, authHeaders);
      if (response.data.success) {
        setAiQuoteResult(response.data.estimation || response.data.raw_response);
        toast.success("Quote estimate generated!");
      }
    } catch (error) {
      console.error("AI quote error:", error);
      toast.error("Failed to generate estimate");
    } finally {
      setAiQuoteLoading(false);
    }
  };

  // AI Job Description
  const handleAiJobDescription = async () => {
    if (!aiDescInput.brief_notes.trim()) {
      toast.error("Please enter some notes");
      return;
    }
    setAiDescLoading(true);
    setAiDescResult('');
    try {
      const response = await axios.post(`${API}/api/admin/ai/job-description`, aiDescInput, authHeaders);
      if (response.data.success) {
        setAiDescResult(response.data.description);
        toast.success("Description generated!");
      }
    } catch (error) {
      console.error("AI description error:", error);
      toast.error("Failed to generate description");
    } finally {
      setAiDescLoading(false);
    }
  };

  // AI Auto Reply
  const handleAiAutoReply = async () => {
    if (!aiReplyInput.enquiry_text.trim()) {
      toast.error("Please enter the enquiry text");
      return;
    }
    setAiReplyLoading(true);
    setAiReplyResult('');
    try {
      const response = await axios.post(`${API}/api/admin/ai/auto-reply`, aiReplyInput, authHeaders);
      if (response.data.success) {
        setAiReplyResult(response.data.reply);
        toast.success("Reply generated!");
      }
    } catch (error) {
      console.error("AI reply error:", error);
      toast.error("Failed to generate reply");
    } finally {
      setAiReplyLoading(false);
    }
  };

  // AI Business Insights
  const handleAiBusinessInsights = async () => {
    setAiInsightsLoading(true);
    setAiInsights(null);
    try {
      const response = await axios.get(`${API}/api/admin/ai/business-insights`, authHeaders);
      if (response.data.success) {
        setAiInsights(response.data);
        toast.success("Insights generated!");
      }
    } catch (error) {
      console.error("AI insights error:", error);
      toast.error("Failed to generate insights");
    } finally {
      setAiInsightsLoading(false);
    }
  };

  // AI Follow-ups
  const handleAiFollowUps = async () => {
    setAiFollowUpsLoading(true);
    setAiFollowUps([]);
    try {
      const response = await axios.get(`${API}/api/admin/ai/follow-up-suggestions`, authHeaders);
      if (response.data.success) {
        setAiFollowUps(response.data.suggestions || []);
        toast.success("Follow-up suggestions ready!");
      }
    } catch (error) {
      console.error("AI follow-ups error:", error);
      toast.error("Failed to generate suggestions");
    } finally {
      setAiFollowUpsLoading(false);
    }
  };

  // AI Strategic Insights
  const handleAiStrategicInsights = async () => {
    setAiStrategicLoading(true);
    setAiStrategicResult(null);
    try {
      const response = await axios.get(`${API}/api/admin/ai/strategic-insights`, authHeaders);
      if (response.data.success) {
        setAiStrategicResult(response.data);
        toast.success("Strategic recommendations ready!");
      }
    } catch (error) {
      console.error("AI strategic insights error:", error);
      toast.error("Failed to generate strategic insights");
    } finally {
      setAiStrategicLoading(false);
    }
  };

  // AI Customer Outreach
  const handleAiOutreach = async () => {
    if (!aiOutreachCustomer) {
      toast.error("Please enter a customer name");
      return;
    }
    setAiOutreachLoading(true);
    setAiOutreachResult(null);
    try {
      const response = await axios.post(
        `${API}/api/admin/ai/customer-outreach?customer_name=${encodeURIComponent(aiOutreachCustomer)}&outreach_type=${aiOutreachType}`,
        {},
        authHeaders
      );
      if (response.data.success) {
        setAiOutreachResult(response.data);
        toast.success("Outreach message generated!");
      }
    } catch (error) {
      console.error("AI outreach error:", error);
      if (error.response?.status === 404) {
        toast.error("Customer not found. Try a different name.");
      } else {
        toast.error("Failed to generate outreach message");
      }
    } finally {
      setAiOutreachLoading(false);
    }
  };

  // AI Service Recommendations
  const handleAiServiceRecs = async () => {
    setAiServiceRecsLoading(true);
    setAiServiceRecs(null);
    try {
      const response = await axios.get(`${API}/api/admin/ai/service-recommendations`, authHeaders);
      if (response.data.success) {
        setAiServiceRecs(response.data);
        toast.success("Service recommendations ready!");
      }
    } catch (error) {
      console.error("AI service recs error:", error);
      toast.error("Failed to generate service recommendations");
    } finally {
      setAiServiceRecsLoading(false);
    }
  };

  // Weekly Report Functions
  const fetchWeeklyReport = async () => {
    setWeeklyReportLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/reports/weekly-summary`, authHeaders);
      setWeeklyReport(response.data);
    } catch (error) {
      console.error("Weekly report error:", error);
      toast.error("Failed to load weekly report");
    } finally {
      setWeeklyReportLoading(false);
    }
  };

  const sendWeeklyEmail = async () => {
    setSendingWeeklyEmail(true);
    try {
      const response = await axios.post(`${API}/api/admin/reports/send-weekly-email`, {}, authHeaders);
      if (response.data.success) {
        toast.success(`Weekly report sent to ${response.data.recipients?.length || 1} admin(s)!`);
      }
    } catch (error) {
      console.error("Send weekly email error:", error);
      toast.error("Failed to send weekly report email");
    } finally {
      setSendingWeeklyEmail(false);
    }
  };

  // Handle creating quote from AI estimate
  const handleCreateQuoteFromEstimate = () => {
    if (onCreateQuote && aiQuoteResult) {
      onCreateQuote({
        line_items: aiQuoteResult.line_items?.map(item => ({
          description: item.description,
          quantity: item.quantity || 1,
          unit_cost: item.unit_cost || 0
        })) || [{ description: '', quantity: 1, unit_cost: 0 }],
        description: aiQuoteInput.description
      });
      toast.success("Quote form pre-filled with estimate");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
          <Sparkles className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-heading font-bold">AI Assistant</h2>
          <p className="text-muted-foreground">Powered by OpenAI GPT-5.5</p>
        </div>
      </div>

      {/* Business data chat */}
      <AIBusinessChat authHeaders={authHeaders} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quote Estimation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-purple-600" />
              Quote Estimation
            </CardTitle>
            <p className="text-sm text-muted-foreground">Get AI-suggested pricing based on past jobs</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Job Type</Label>
              <Select value={aiQuoteInput.job_type} onValueChange={(v) => setAiQuoteInput(p => ({ ...p, job_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="carpentry">Carpentry</SelectItem>
                  <SelectItem value="general-repairs">General Repairs</SelectItem>
                  <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                  <SelectItem value="garden-storage">Garden Storage</SelectItem>
                  <SelectItem value="garden-buildings">Garden Buildings</SelectItem>
                  <SelectItem value="fencing-gates">Fencing & Gates</SelectItem>
                  <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Job Description</Label>
              <Textarea 
                value={aiQuoteInput.description}
                onChange={(e) => setAiQuoteInput(p => ({ ...p, description: e.target.value }))}
                placeholder="Describe the job... e.g., Build a 3m x 2m garden shed with double doors and a window"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="include-materials"
                checked={aiQuoteInput.include_materials}
                onChange={(e) => setAiQuoteInput(p => ({ ...p, include_materials: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="include-materials" className="cursor-pointer">Include materials estimate</Label>
            </div>
            <Button onClick={handleAiQuoteEstimate} disabled={aiQuoteLoading} className="w-full gap-2">
              {aiQuoteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              Generate Estimate
            </Button>
            
            {aiQuoteResult && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                {typeof aiQuoteResult === 'object' ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-purple-800">Estimated Total: £{aiQuoteResult.estimated_total?.toFixed(2)}</span>
                      <Badge className={aiQuoteResult.confidence === 'high' ? 'bg-green-100 text-green-800' : aiQuoteResult.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                        {aiQuoteResult.confidence} confidence
                      </Badge>
                    </div>
                    {aiQuoteResult.line_items && (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium">Suggested line items:</p>
                        {aiQuoteResult.line_items.map((item, i) => (
                          <div key={i} className="flex justify-between text-muted-foreground">
                            <span>{item.description}</span>
                            <span>£{item.unit_cost?.toFixed(2)} x {item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">{aiQuoteResult.reasoning}</p>
                    
                    {/* Generate Quote Button */}
                    <div className="pt-3 border-t border-purple-200 space-y-3">
                      {/* Customer Selection */}
                      <div>
                        <Label className="text-sm">Select Customer (optional)</Label>
                        <Select onValueChange={(v) => {
                          const customer = customers.find(c => c.id === v);
                          if (customer && onCreateQuote) {
                            // Will be handled by parent
                          }
                        }}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select existing customer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {customers.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name} - {c.address}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleCreateQuoteFromEstimate}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 gap-2"
                        >
                          <FileText className="h-4 w-4" />
                          Generate Quote
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap text-sm">{aiQuoteResult}</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Job Description Generator */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Job Description Generator
            </CardTitle>
            <p className="text-sm text-muted-foreground">Turn brief notes into professional descriptions</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Job Type</Label>
              <Select value={aiDescInput.job_type} onValueChange={(v) => setAiDescInput(p => ({ ...p, job_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="carpentry">Carpentry</SelectItem>
                  <SelectItem value="general-repairs">General Repairs</SelectItem>
                  <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                  <SelectItem value="garden-storage">Garden Storage</SelectItem>
                  <SelectItem value="garden-buildings">Garden Buildings</SelectItem>
                  <SelectItem value="fencing-gates">Fencing & Gates</SelectItem>
                  <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Brief Notes</Label>
              <Textarea 
                value={aiDescInput.brief_notes}
                onChange={(e) => setAiDescInput(p => ({ ...p, brief_notes: e.target.value }))}
                placeholder="E.g., Fix kitchen cabinet doors, broken hinge, repaint"
                rows={3}
              />
            </div>
            <Button onClick={handleAiJobDescription} disabled={aiDescLoading} className="w-full gap-2">
              {aiDescLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              Generate Description
            </Button>
            
            {aiDescResult && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">Generated Description</span>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiDescResult)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm whitespace-pre-wrap">{aiDescResult}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-Reply Generator */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" />
              Customer Reply Generator
            </CardTitle>
            <p className="text-sm text-muted-foreground">Generate professional responses to customer enquiries</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Customer Name (optional)</Label>
                <Input 
                  value={aiReplyInput.customer_name}
                  onChange={(e) => setAiReplyInput(p => ({ ...p, customer_name: e.target.value }))}
                  placeholder="John"
                />
              </div>
              <div>
                <Label>Service Type</Label>
                <Select value={aiReplyInput.service_type} onValueChange={(v) => setAiReplyInput(p => ({ ...p, service_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carpentry">Carpentry</SelectItem>
                    <SelectItem value="general-repairs">General Repairs</SelectItem>
                    <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                    <SelectItem value="garden-storage">Garden Storage</SelectItem>
                    <SelectItem value="garden-buildings">Garden Buildings</SelectItem>
                    <SelectItem value="fencing-gates">Fencing & Gates</SelectItem>
                    <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Customer Enquiry</Label>
              <Textarea 
                value={aiReplyInput.enquiry_text}
                onChange={(e) => setAiReplyInput(p => ({ ...p, enquiry_text: e.target.value }))}
                placeholder="Paste the customer's message here..."
                rows={3}
              />
            </div>
            <Button onClick={handleAiAutoReply} disabled={aiReplyLoading} className="gap-2">
              {aiReplyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              Generate Reply
            </Button>
            
            {aiReplyResult && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-800">Suggested Reply</span>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiReplyResult)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm whitespace-pre-wrap">{aiReplyResult}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Business Intelligence Section */}
      <div className="flex items-center gap-3 pt-4">
        <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
          <Target className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Business Intelligence</h3>
          <p className="text-sm text-muted-foreground">AI-powered insights and recommendations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Insights */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                  Business Insights
                </CardTitle>
                <p className="text-sm text-muted-foreground">Get AI analysis of your business performance</p>
              </div>
              <Button onClick={handleAiBusinessInsights} disabled={aiInsightsLoading} className="gap-2">
                {aiInsightsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Analyze
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {aiInsights ? (
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-emerald-600" />
                    AI Analysis
                  </h4>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiInsights.insights || '')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="prose prose-sm max-w-none text-sm whitespace-pre-wrap">{aiInsights.insights}</div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="text-center text-muted-foreground">
                  <TrendingUp className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-sm font-medium mb-1">Business Insights</p>
                  <p className="text-xs mb-4">AI analyzes your jobs, quotes, invoices and customer data to provide actionable insights</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                  <p className="font-medium text-muted-foreground">Example insights you'll receive:</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground/80 text-xs">
                    <li>Quote conversion rate trends and recommendations</li>
                    <li>Most profitable service categories</li>
                    <li>Customer follow-up opportunities</li>
                    <li>Seasonal patterns and forecasts</li>
                    <li>Payment collection optimization tips</li>
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Follow-up Suggestions */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Follow-up Suggestions
                </CardTitle>
                <p className="text-sm text-muted-foreground">AI identifies customers needing attention</p>
              </div>
              <Button onClick={handleAiFollowUps} disabled={aiFollowUpsLoading} className="gap-2">
                {aiFollowUpsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Get Suggestions
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {aiFollowUps.length > 0 ? (
              <div className="space-y-3">
                {aiFollowUps.map((item, i) => (
                  <div key={i} className={`p-3 rounded-lg border-l-4 ${
                    item.priority === 'high' ? 'bg-red-50 border-red-500' :
                    item.priority === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                    'bg-gray-50 border-gray-400'
                  }`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-sm text-muted-foreground">{item.reason}</p>
                        <p className="text-sm mt-1"><span className="font-medium">Action:</span> {item.action}</p>
                      </div>
                      <Badge className={
                        item.priority === 'high' ? 'bg-red-100 text-red-800' :
                        item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {item.priority}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">Click Get Suggestions to identify customers needing follow-up</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Strategic AI Section Divider */}
      <div className="flex items-center gap-3 pt-4">
        <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
          <Crown className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Strategic AI</h3>
          <p className="text-sm text-muted-foreground">Advanced business intelligence and recommendations</p>
        </div>
      </div>

      {/* Strategic Insights */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-600" />
                Strategic Recommendations
              </CardTitle>
              <p className="text-sm text-muted-foreground">AI analyzes your business data for growth opportunities</p>
            </div>
            <Button onClick={handleAiStrategicInsights} disabled={aiStrategicLoading} className="gap-2" data-testid="ai-strategic-btn">
              {aiStrategicLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Analyze Business
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {aiStrategicResult ? (
            <div className="space-y-4">
              {/* Key Metrics Summary */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-emerald-700">£{aiStrategicResult.key_metrics?.total_revenue?.toLocaleString() || 0}</p>
                  <p className="text-xs text-emerald-600">Total Revenue</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-blue-700">{aiStrategicResult.key_metrics?.total_customers || 0}</p>
                  <p className="text-xs text-blue-600">Customers</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-purple-700">{aiStrategicResult.key_metrics?.quote_conversion || 0}%</p>
                  <p className="text-xs text-purple-600">Quote Conversion</p>
                </div>
              </div>
              
              {/* AI Recommendations */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-600" />
                    AI Strategic Analysis
                  </h4>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiStrategicResult.recommendations)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="prose prose-sm max-w-none text-sm whitespace-pre-wrap">{aiStrategicResult.recommendations}</div>
              </div>

              {/* Dormant Customers Alert */}
              {aiStrategicResult.dormant_customers?.length > 0 && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    Customers to Re-engage ({aiStrategicResult.dormant_customers.length})
                  </h4>
                  <div className="space-y-2">
                    {aiStrategicResult.dormant_customers.slice(0, 5).map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-white rounded">
                        <span className="font-medium">{c.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-orange-600">{c.days} days ago</Badge>
                          {c.email && <Button variant="ghost" size="sm" asChild><a href={`mailto:${c.email}`}><Mail className="h-3 w-3" /></a></Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="text-center text-muted-foreground">
                <BarChart3 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm font-medium mb-1">Strategic Analysis</p>
                <p className="text-xs mb-4">AI analyzes your business performance and suggests growth opportunities</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="font-medium text-muted-foreground text-xs mb-2">Revenue Analysis</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground/80 text-xs">
                    <li>Monthly trends and forecasts</li>
                    <li>Best performing services</li>
                    <li>Average job values</li>
                  </ul>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="font-medium text-muted-foreground text-xs mb-2">Customer Insights</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground/80 text-xs">
                    <li>Dormant customers to re-engage</li>
                    <li>Top repeat customers</li>
                    <li>Geographic opportunities</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Outreach Generator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Customer Outreach
          </CardTitle>
          <p className="text-sm text-muted-foreground">Generate personalized messages to reach out to customers</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                value={aiOutreachCustomer}
                onChange={(e) => setAiOutreachCustomer(e.target.value)}
                placeholder="Enter customer name..."
                data-testid="outreach-customer-input"
              />
            </div>
            <div>
              <Label>Message Type</Label>
              <Select value={aiOutreachType} onValueChange={setAiOutreachType}>
                <SelectTrigger data-testid="outreach-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="re-engagement">Re-engagement (win back)</SelectItem>
                  <SelectItem value="thank-you">Thank You (loyalty)</SelectItem>
                  <SelectItem value="follow-up">Follow-up (after job)</SelectItem>
                  <SelectItem value="seasonal">Seasonal Offer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleAiOutreach} disabled={aiOutreachLoading} className="gap-2" data-testid="generate-outreach-btn">
            {aiOutreachLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Generate Message
          </Button>

          {aiOutreachResult && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <Badge>{aiOutreachResult.outreach_type}</Badge>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiOutreachResult.message)}>
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                For: {aiOutreachResult.customer?.name} • Total spent: £{aiOutreachResult.customer?.total_spent?.toLocaleString() || 0}
              </p>
              <div className="text-sm whitespace-pre-wrap bg-white p-3 rounded border">{aiOutreachResult.message}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Recommendations */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Hammer className="h-5 w-5 text-green-600" />
                Service Recommendations
              </CardTitle>
              <p className="text-sm text-muted-foreground">AI suggests service improvements and new opportunities</p>
            </div>
            <Button onClick={handleAiServiceRecs} disabled={aiServiceRecsLoading} className="gap-2" data-testid="ai-service-recs-btn">
              {aiServiceRecsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Get Recommendations
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {aiServiceRecs ? (
            <div className="space-y-4">
              {/* Current Services Analysis */}
              {aiServiceRecs.current_services?.by_revenue && Object.keys(aiServiceRecs.current_services.by_revenue).length > 0 && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">Your Top Services by Revenue</h4>
                  <div className="space-y-1">
                    {Object.entries(aiServiceRecs.current_services.by_revenue).slice(0, 5).map(([service, revenue], i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span>{service}</span>
                        <Badge variant="outline">£{revenue?.toLocaleString()}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* AI Recommendations */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-green-600" />
                    AI Service Analysis
                  </h4>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiServiceRecs.recommendations)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="prose prose-sm max-w-none text-sm whitespace-pre-wrap">{aiServiceRecs.recommendations}</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Click Get Recommendations to analyze your service offerings</p>
          )}
        </CardContent>
      </Card>

      {/* Weekly Report Section */}
      <div className="flex items-center gap-3 pt-4">
        <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
          <BarChart3 className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Weekly Reports</h3>
          <p className="text-sm text-muted-foreground">Automated business summaries delivered to your inbox</p>
        </div>
      </div>

      {/* Scheduler Status Card */}
      <Card className="border-green-200 bg-green-50/30">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-full">
                <Clock className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">Automated Reports Enabled</p>
                <p className="text-sm text-green-600">Reports sent every Monday at 8:00 AM UK time</p>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-800">Active</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-indigo-600" />
                Weekly Business Summary
              </CardTitle>
              <p className="text-sm text-muted-foreground">Preview and send your weekly report to all admins</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchWeeklyReport} disabled={weeklyReportLoading} data-testid="preview-report-btn">
                {weeklyReportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                <span className="ml-2">Preview</span>
              </Button>
              <Button onClick={sendWeeklyEmail} disabled={sendingWeeklyEmail || !weeklyReport} data-testid="send-report-btn">
                {sendingWeeklyEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                <span className="ml-2">Send Now</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {weeklyReport ? (
            <div className="space-y-4">
              {/* Highlights */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-emerald-700">£{weeklyReport.highlights?.revenue?.toLocaleString() || 0}</p>
                  <p className="text-xs text-emerald-600">Revenue</p>
                  {weeklyReport.highlights?.revenue_change !== 0 && (
                    <p className={`text-xs ${weeklyReport.highlights?.revenue_change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {weeklyReport.highlights?.revenue_change >= 0 ? '+' : ''}£{weeklyReport.highlights?.revenue_change?.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-blue-700">{weeklyReport.highlights?.enquiries || 0}</p>
                  <p className="text-xs text-blue-600">Enquiries</p>
                  {weeklyReport.highlights?.enquiries_change !== 0 && (
                    <p className={`text-xs ${weeklyReport.highlights?.enquiries_change >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                      {weeklyReport.highlights?.enquiries_change >= 0 ? '+' : ''}{weeklyReport.highlights?.enquiries_change}
                    </p>
                  )}
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-amber-700">{weeklyReport.highlights?.jobs_completed || 0}</p>
                  <p className="text-xs text-amber-600">Jobs Done</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-purple-700">{weeklyReport.highlights?.quotes_sent || 0}</p>
                  <p className="text-xs text-purple-600">Quotes Sent</p>
                </div>
              </div>

              {/* Pending & Upcoming */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-600" />
                    <span title="Quote documents awaiting a customer response (draft, pending, sent or viewed) — same definition as Dashboard Quote Pipeline and Analytics Pending. All time.">Pending Quotes</span>
                  </h4>
                  <p className="text-lg font-bold">{weeklyReport.pending?.quotes_count || 0} quotes</p>
                  <p className="text-sm text-muted-foreground">Worth £{weeklyReport.pending?.quotes_value?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-indigo-600" />
                    <span title="Next calendar bookings (blocked days of type job), from today onwards.">Upcoming Jobs</span>
                  </h4>
                  {weeklyReport.upcoming_jobs?.length > 0 ? (
                    <div className="space-y-1 text-sm">
                      {weeklyReport.upcoming_jobs.slice(0, 3).map((job, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="truncate">{job.customer || job.title}</span>
                          <Badge variant="outline" className="text-xs">{job.date}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No upcoming jobs</p>
                  )}
                </div>
              </div>

              {/* Recent Feedback */}
              {weeklyReport.recent_feedback?.length > 0 && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-600" />
                    Recent Reviews
                  </h4>
                  <div className="space-y-2">
                    {weeklyReport.recent_feedback.map((f, i) => (
                      <div key={i} className="text-sm">
                        <span className="font-medium">{f.customer}</span>: {'⭐'.repeat(f.rating)} 
                        {f.comment && <span className="text-muted-foreground ml-1">- {f.comment}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All-Time Totals */}
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">
                  All-Time: <strong>{weeklyReport.totals?.total_customers || 0}</strong> customers • 
                  <strong> £{weeklyReport.totals?.total_revenue?.toLocaleString() || 0}</strong> revenue • 
                  <strong> {weeklyReport.totals?.total_jobs || 0}</strong> jobs
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Click Preview to see this weeks business summary</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIAssistantTab;
